import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
 Loader2,
 Upload,
 CheckCircle2,
 XCircle,
 FileText,
 Download,
 ArrowLeft,
} from "lucide-react";
import Loader from "../../components/ui/Loader";
import { Textarea } from "../../components/ui/textarea";



export default function ApplierProfileForm() {
 const navigate = useNavigate();
 const [profile, setProfile] = useState({
 fullName: "",
 education: "",
 phone: "",
 university: "",
 gpa: "",
 gender: "",
 telegramUsername: "",
 resume: null,
 resumeUrl: "",
 });
 const [resumeName, setResumeName] = useState("");
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [message, setMessage] = useState("");
 const [error, setError] = useState("");

 useEffect(() => {
 const fetchProfile = async () => {
 try {
 const res = await api.get("/applier/profile");
 if (res.data) {
 const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
 const telegramUsername = telegramUser?.username
 ? `@${telegramUser.username}`
 : "";

 setProfile({
 fullName: res.data.fullName || "",
 education: res.data.education || "",
 phone: res.data.phone || "",
 university: res.data.university || "",
 gpa: res.data.gpa || "",
 gender: res.data.gender || "",
 telegramUsername: res.data.telegramUsername || telegramUsername,
 resume: null,
 resumeUrl: res.data.resumeUrl || "",
 });
 }
 } catch {
 setError("No existing profile found.");
 } finally {
 setLoading(false);
 }
 };
 fetchProfile();
 }, []);

 const handleChange = (e) => {
 const { name, value, files } = e.target;
 if (files && files[0]) {
 const file = files[0];
 const maxSize = 2 * 1024 * 1024; // 2MB in bytes
 
 if (file.size > maxSize) {
 setError(`File size exceeds 2MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB. Please choose a smaller file.`);
 // Reset the file input
 e.target.value = '';
 return;
 }
 
 setProfile((prev) => ({ ...prev, resume: file }));
 setResumeName(file.name);
 setError(''); // Clear any previous errors
 } else {
 setProfile((prev) => ({ ...prev, [name]: value }));
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setSaving(true);
 setMessage("");
 setError("");

 try {
 const formData = new FormData();
 formData.append("fullName", profile.fullName);
 formData.append("education", profile.education);
 formData.append("phone", profile.phone);
 formData.append("university", profile.university);
 formData.append("gpa", profile.gpa);
 formData.append("gender", profile.gender);
 formData.append("telegramUsername", profile.telegramUsername || "");
 if (profile.resume) formData.append("resume", profile.resume);

 const res = await api.put("/applier/profile", formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 setMessage(res.data.message || "Profile saved successfully!");
 setProfile((prev) => ({
 ...prev,
 resumeUrl: res.data.profile.resumeUrl || "",
 }));
 setResumeName("");
 } catch (err) {
 console.error(err);
 setError(
 err.response?.data?.message ||
 "Failed to save profile. Please try again."
 );
 } finally {
 setSaving(false);
 }
 };

 if (loading) return <Loader />;

 return (
 <div className="min-h-screen py-8 sm:py-12 px-4">
 <div className="max-w-3xl mx-auto space-y-6">
 <div className="flex items-center gap-4">
 <Button
 variant="ghost"
 onClick={() => navigate(-1)}
 className="flex items-center gap-2"
 >
 <ArrowLeft className="w-4 h-4" />
 Back
 </Button>
 <div className="flex-1">
 <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
 Edit Profile
 </h1>
 <p className="text-sm sm:text-base text-muted-foreground mt-1">
 Update your information and upload your resume
 </p>
 </div>
 </div>

 {message && (
 <Alert className="border-green-200 bg-green-500/10 dark:border-green-800">
 <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
 <AlertDescription className="text-green-800 dark:text-green-200">
 {message}
 </AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive">
 <XCircle className="h-4 w-4" />
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 <Card className="border-2">
 <CardHeader>
 <CardTitle className="text-xl sm:text-2xl font-semibold">
 Profile Details
 </CardTitle>
 </CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-5">
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
 <InputField
 label="Full Name"
 name="fullName"
 value={profile.fullName}
 onChange={handleChange}
 required
 />
 <InputField
 label="Phone Number"
 name="phone"
 value={profile.phone}
 onChange={handleChange}
 required
 />
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
 <InputField
 label="University"
 name="university"
 value={profile.university}
 onChange={handleChange}
 required
 />
 <InputField
 label="Education"
 name="education"
 value={profile.education}
 onChange={handleChange}
 placeholder="e.g., Bachelor's in Computer Science"
 required
 />
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
 <InputField
 label="GPA"
 name="gpa"
 type="number"
 step="0.01"
 value={profile.gpa}
 onChange={handleChange}
 placeholder="e.g., 3.75"
 required
 />

 <div className="space-y-2">
 <label className="text-sm font-medium text-foreground">
 Gender
 </label>
 <select
 name="gender"
 value={profile.gender}
 onChange={handleChange}
 required
 className="w-full h-10 border border-input rounded-lg px-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition"
 >
 <option value="">Select gender</option>
 <option value="MALE">Male</option>
 <option value="FEMALE">Female</option>
 </select>
 </div>
 </div>

 <div className="space-y-2">
 <label className="text-sm font-medium text-foreground">
 Telegram Username <span className="text-muted-foreground text-xs">(Optional)</span>
 </label>
 <Input
 name="telegramUsername"
 value={profile.telegramUsername}
 onChange={handleChange}
 placeholder="@username"
 className="h-10"
 />
 <p className="text-xs text-muted-foreground">
 For future contact via Telegram
 </p>
 </div>

 <div className="space-y-3">
 <label className="text-sm font-medium text-foreground">
 Upload Resume (PDF) <span className="text-xs text-muted-foreground">(Max 2MB)</span>
 </label>
 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
 <label
 htmlFor="resume"
 className="flex items-center justify-center px-4 py-2 h-10 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover: hover:border-primary/50 transition"
 >
 <Upload className="w-4 h-4 mr-2" />
 <span className="font-medium text-sm">Choose File</span>
 </label>
 <input
 id="resume"
 type="file"
 name="resume"
 accept=".pdf"
 className="hidden"
 onChange={handleChange}
 />
 {resumeName && (
 <div className="flex items-center gap-2 px-3 py-1.5 rounded-md">
 <FileText className="w-4 h-4 text-primary" />
 <p className="text-sm font-medium truncate max-w-[200px]">
 {resumeName}
 </p>
 </div>
 )}
 </div>
 {profile.resumeUrl && (
 <div className="flex flex-col sm:flex-row gap-2 pt-2">
 <Button
 type="button"
 variant="outline"
 size="sm"
 className="w-full sm:w-auto gap-2"
 onClick={() => window.open(
 `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${profile.resumeUrl.startsWith('/') ? profile.resumeUrl.substring(1) : profile.resumeUrl}`,
 '_blank'
 )}
 >
 <FileText className="w-4 h-4" />
 View Current Resume
 </Button>
 <Button
 type="button"
 variant="outline"
 size="sm"
 className="w-full sm:w-auto gap-2"
 onClick={() => window.open(
 `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${profile.resumeUrl.startsWith('/') ? profile.resumeUrl.substring(1) : profile.resumeUrl}`,
 '_blank'
 )}
 >
 <Download className="w-4 h-4" />
 Download
 </Button>
 </div>
 )}
 </div>

 <Button
 type="submit"
 disabled={saving}
 className="w-full h-11 font-semibold shadow-lg"
 >
 {saving ? (
 <>
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 Saving...
 </>
 ) : (
 "Save Profile"
 )}
 </Button>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 );
}

function InputField({ label, ...props }) {
 return (
 <div className="space-y-2">
 <label className="text-sm font-medium text-foreground">
 {label}
 </label>
 <Input
 {...props}
 className="h-10"
 />
 </div>
 );
}
